import { Autocomplete, Chip, TextField } from '@mui/material'
import type { Token } from '@stripe/stripe-js'
import dynamic from 'next/dynamic'
import { useEffect, useState } from 'react'
import Button from '#/components/Button'
import Checkbox from '#/components/Checkbox'
import { reportPrice } from '#/configs/reports'
import { logEvent } from '#/services/analytics'
import { reportName } from '#/utils/report'

const PaymentForm = dynamic(() => import('#/components/PaymentForm'))

type PaymentInformationProps = {
  authenticated: boolean
  loading?: boolean
  reportType: TopHap.ReportType
  property: TopHap.Property
  onChangeModal: (
    modal: {
      show: boolean
      type: 'Payment' | 'Customize'
    }
  ) => void
  onUpgrade: () => void
  onSubmit: (
    reportType: TopHap.ReportType,
    token: Token,
    receiptEmail: string,
    recipients: string[],
    name: string,
    signUp: boolean) => void
}

const PaymentInformation: React.FC<PaymentInformationProps> = ({
  authenticated,
  loading,
  reportType,
  property,
  onUpgrade,
  onChangeModal,
  onSubmit
}) => {
  const [recipients, setRecipients] = useState<string[]>([])
  const [signUp, showSignUp] = useState(true)
  const [input, setInput] = useState('')
  const price = reportPrice[reportType]

  useEffect(() => {
    logEvent('begin_checkout', {
      items: [{
        item_id: `report_${reportType.toLowerCase()}`,
        item_name: reportType.toLowerCase(),
        quantity: 1,
        price
      }]
    })
  }, [reportType])

  const handleSubmit = (token: Token, receiptEmail: string, name: string) => {
    onSubmit(reportType, token, receiptEmail, recipients, name, signUp)

    logEvent('select_content', {
      content_type: 'property_detail',
      item_id: 'report/pay',
      value: reportType,
      email: receiptEmail,
      sign_up: signUp
    })
  }

  const handleBlur = () => {
    if (input) {
      setRecipients([...recipients, ...input.split(/\s*,\s*|\s+/)].filter(e => e))
    }
  }

  const forceDownload = (url: string) => {
    fetch(url).then(t => t.blob().then(b => {
      const a = document.createElement('a')
      a.href = URL.createObjectURL(b)
      a.setAttribute('download', 'TopHap-Full-Property-Report-Sample')
      a.click()
    }))
  }

  return (
    <div>
      <h3 className='text-xl font-bold text-neutral-800'>{reportName(reportType)}</h3>
      <p className='my-2 text-sm'>
        <span>
          {property.displayAddress}, {property.displayRegion}
        </span>
        <br />
        <button
          className='text-primary underline hover:underline'
          onClick={() => forceDownload('https://assets.tophap.com/cms/TopHap-Full-Property-Report-Sample-1.pdf')}
        >
          Download Sample Report
        </button>
      </p>

      <span className='text-sm font-semibold'>Payment Information</span>
      {/* Credit Card Information */}
      <PaymentForm id='payment_form' onSubmit={handleSubmit} />

      {/* Sign Up Information */}
      <div className='mb-1 mt-2'>
        {authenticated ?
          (
            <Button className='rounded p-1 text-sm text-primary underline' onClick={onUpgrade}>
              Upgrade for unlimited free reports
            </Button>
          ) :
          (
            <Checkbox
              checked={signUp}
              classes={{ label: '!text-sm' }}
              label='Create an account for easy report access'
              onChange={(ev, checked) => showSignUp(checked)}
            />
          )}
      </div>

      {/* Email Recipients */}
      <div>
        <span className='mb-1 text-xs'>Add email recipients separated by commas (optional)</span>
        <Autocomplete
          clearOnBlur
          freeSolo
          multiple
          options={[]}
          renderInput={params => (<TextField {...params} size='small' />)}
          renderTags={(value: readonly string[], getTagProps) =>
            value.map((option: string, index: number) => (
              // eslint-disable-next-line react/jsx-key
              <Chip label={option} variant='outlined' {...getTagProps({ index })} />
            ))}
          size='small'
          value={recipients}
          onBlur={handleBlur}
          onChange={(event, value: string[]) => setRecipients(value.map(e => e.split(/\s*,\s*|\s+/)).flat().filter(e => e))}
          onInputChange={(_, value) => setInput(value)}
        />
      </div>

      <div className='my-2 text-right text-sm font-semibold'>
        Total Price: $
        {price}
      </div>
      <div className='my-2 text-[10px]'>
        By clicking 'Purchase and Download' you agree to have your payment information
        above charged for the 'Total Price' and to have your report automatically
        downloaded and optionally sent to email recipients you have listed above.
      </div>
      <div className='mt-4 flex justify-end'>
        <Button className='mr-2 rounded p-1 text-sm text-primary underline' onClick={() => onChangeModal({ show: true, type: 'Customize' })}>
          Customize Report
        </Button>
        <Button
          className='h-10 rounded-md bg-gradient-to-r from-primary to-gradient-blue px-3 text-sm text-white'
          form='payment_form'
          loading={loading}
          type='submit'
        >
          Purchase and Download
        </Button>
      </div>
    </div>
  )
}

export default PaymentInformation
